import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { chatApi } from "apis";
import {
  AddContactRequest,
  CheckContactContactTypeEnum,
  CreateConversationRequest,
} from "libs/cellera-sdk";

export const useCreateConversation = () => {
  return useMutation({
    mutationFn: (request: CreateConversationRequest) =>
      chatApi.createConversation(request),
    onError: (error) => {
      console.error("Error creating conversation:", error);
      throw new Error(error.message);
    },
  });
};

export const useConversations = (enabled: boolean = true) => {
  return useInfiniteQuery({
    queryKey: ["conversations"],
    queryFn: ({ pageParam = 1 }) =>
      chatApi.getConversations({
        page: pageParam,
        pageSize: 10,
      }),
    enabled,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, _, currentPage) => {
      return lastPage.pagination.hasMore ? currentPage + 1 : undefined;
    },
    initialPageParam: 1,
    throwOnError: (error: Error) => {
      console.error("Error fetching conversations:", error);
      throw new Error(error.message);
    },
  });
};

export const useConversationHistory = (conversationId: string) => {
  return useInfiniteQuery({
    queryKey: ["messages", conversationId],
    queryFn: ({ pageParam = 1 }) =>
      chatApi.getMessages({
        id: conversationId,
        page: pageParam,
        pageSize: 10,
      }),
    getNextPageParam: (lastPage, _, currentPage) => {
      return lastPage.pagination.hasMore ? currentPage + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: !!conversationId,
    placeholderData: keepPreviousData,
    throwOnError: (error: Error) => {
      console.error("Error fetching conversation history:", error);
      throw new Error(error.message);
    },
  });
};

export const useUserPresenceStatus = (userId: string) => {
  return useQuery({
    queryKey: ["user-presence", { userId }],
    queryFn: () => chatApi.getUserPresence({ userId }),
    throwOnError: (error: Error) => {
      console.error("Error fetching user presence:", error);
      throw new Error(error.message);
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    enabled: !!userId,
  });
};

export const useContacts = (enabled: boolean = true, search?: string) => {
  return useInfiniteQuery({
    queryKey: ["contacts", search],
    queryFn: ({ pageParam = 1 }) =>
      chatApi.getContacts({
        search: search,
        page: pageParam,
        pageSize: 10,
      }),
    getNextPageParam: (lastPage, _, currentPage) => {
      return lastPage.pagination.hasMore ? currentPage + 1 : undefined;
    },
    initialPageParam: 1,
    enabled,
    placeholderData: keepPreviousData,
    throwOnError: (error: Error) => {
      console.error("Error fetching contacts:", error);
      throw new Error(error.message);
    },
  });
};

export const useAddContact = () => {
  return useMutation({
    mutationFn: (request: AddContactRequest) => chatApi.addContact(request),
    onError: (error) => {
      console.error("Error adding contact:", error.message);
      throw new Error(error.message);
    },
  });
};

export const useRemoveContact = () => {
  return useMutation({
    mutationFn: (contactId: string) =>
      chatApi.deleteContact({
        addContactDto: {
          contactId,
          contactType: CheckContactContactTypeEnum.Patient,
        },
      }),
    onError: (error) => {
      console.error("Error removing contact:", error.message);
      throw new Error(error.message);
    },
  });
};

// Check if a patient or provider is already in the user's contact list
export const useCheckContactExist = (userId: string) => {
  return useQuery({
    queryKey: ["check-contact-exists", userId],
    queryFn: () =>
      chatApi.checkContact({
        contactId: userId,
        contactType: CheckContactContactTypeEnum.Patient,
      }),
    enabled: !!userId,
    throwOnError: (error: Error) => {
      console.error("Error checking if contact exists:", error);
      throw new Error(error.message);
    },
  });
};
